// apps/web/src/pages/admin/AdminUsers.tsx
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { Admin, AddAdminInput } from '@hc-alumni/shared/types';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';
import {
  Plus,
  Trash2,
  Mail,
  User,
  Loader2,
  X,
  Search,
  Shield,
  ShieldCheck,
} from 'lucide-react';

type Role = 'owner' | 'moderator';

type AlumniPick = {
  id: string;
  name: string;
  department?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  isVerified?: boolean;
};

export default function AdminUsers() {
  const queryClient = useQueryClient();
  const me = useAuth((s) => s.user);
  const [showAddModal, setShowAddModal] = useState(false);
  const [role, setRole] = useState<Role>('moderator');
  const [memberSearch, setMemberSearch] = useState('');
  const [selected, setSelected] = useState<AlumniPick | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<Admin | null>(null);

  const { data: admins, isLoading } = useQuery({
    queryKey: ['admins'],
    queryFn: api.admin.getAdmins,
  });

  // 搜尋會員（從後台 alumni 列表）
  const { data: membersData, isFetching: membersLoading } = useQuery({
    queryKey: ['admin-alumni-pick', memberSearch],
    queryFn: () =>
      api.admin.listAlumni({
        page: 1,
        limit: 20,
        status: 'all',
        q: memberSearch.trim() || undefined,
      }),
    enabled: showAddModal,
  });

  const adminAlumniIds = useMemo(() => {
    const set = new Set<string>();
    (admins ?? []).forEach((a) => {
      if (a.alumniId) set.add(a.alumniId);
    });
    return set;
  }, [admins]);

  const candidates = useMemo(() => {
    const items = (membersData?.items ?? []) as AlumniPick[];
    return items.filter((m) => !adminAlumniIds.has(m.id));
  }, [membersData, adminAlumniIds]);

  const addMutation = useMutation({
    mutationFn: (data: AddAdminInput) => api.admin.addAdmin(data),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      setShowAddModal(false);
      setSelected(null);
      setMemberSearch('');
      setRole('moderator');
      toast.success(
        vars.role === 'owner' ? '已新增主管理者' : '已新增副管理者（僅可協助審核）'
      );
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : '新增失敗'),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.admin.removeAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      setConfirmTarget(null);
      toast.success('已移除管理員');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : '刪除失敗'),
  });

  const handleAdd = () => {
    if (!selected) {
      toast.error('請先從會員列表選擇一位');
      return;
    }
    addMutation.mutate({
      alumniId: selected.id,
      role,
      name: selected.name,
    });
  };

  if (isLoading) return <LoadingSpinner size="lg" className="mx-auto my-16" />;

  const list = (admins ?? []) as Admin[];
  const owners = list.filter((a) => (a.role || 'owner') === 'owner');
  const mods = list.filter((a) => a.role === 'moderator');

  return (
    <div className="container-custom">
      <div className="mb-8 animate-fade-in">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">會員管理</h1>
        <p className="text-gray-600 dark:text-gray-400">
          從已註冊會員指派主管理者或副管理者。副管理者僅可協助審核新進人員，不可代編名片。
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 mb-6">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-amber-700 dark:text-amber-200" />
          </div>
          <div>
            <p className="text-sm text-gray-500">主管理者</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{owners.length}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-alumni-100 dark:bg-alumni-900/40 flex items-center justify-center">
            <Shield className="w-5 h-5 text-alumni-700 dark:text-alumni-200" />
          </div>
          <div>
            <p className="text-sm text-gray-500">副管理者（僅審核）</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{mods.length}</p>
          </div>
        </div>
      </div>

      <div className="card animate-slide-up">
        <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            目前管理員 ({list.length})
          </h2>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="btn-primary btn-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            從會員新增
          </button>
        </div>

        {list.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {list.map((admin) => {
              const r = (admin.role || 'owner') as Role;
              const isSelf =
                (admin.alumniId && admin.alumniId === me?.id) ||
                (admin.lineUserId && admin.lineUserId === me?.lineUserId) ||
                (admin.email && me?.email && admin.email === me.email);
              return (
                <div
                  key={admin.id}
                  className="p-4 sm:p-6 flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-alumni-100 flex items-center justify-center shrink-0">
                      <User className="w-6 h-6 text-alumni-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {admin.name || '未設定名稱'}
                          {isSelf ? (
                            <span className="text-xs text-gray-400 font-normal ml-1">（您）</span>
                          ) : null}
                        </p>
                        <RoleBadge role={r} />
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                        {admin.lineUserId && (
                          <span className="font-mono text-xs truncate max-w-[12rem]">
                            LINE: {admin.lineUserId}
                          </span>
                        )}
                        {admin.email && (
                          <span className="inline-flex items-center gap-1 truncate">
                            <Mail className="w-3.5 h-3.5 shrink-0" />
                            {admin.email}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {r === 'owner'
                          ? '可審核、代編、設定、管理管理員'
                          : '僅可協助審核新進人員，不可代編名片'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmTarget(admin)}
                    disabled={removeMutation.isPending || !!isSelf}
                    title={isSelf ? '不能移除自己' : '移除管理員'}
                    className="btn-ghost btn-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                  >
                    {removeMutation.isPending && removeMutation.variables === admin.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <User className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              尚無管理員
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              請從已註冊會員中新增
            </p>
            <button type="button" onClick={() => setShowAddModal(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-1" />
              從會員新增
            </button>
          </div>
        )}
      </div>

      {/* 新增 Modal：從會員選擇 */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col animate-scale-in">
            <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                從會員新增管理員
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setSelected(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="label">角色</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setRole('moderator')}
                    className={`rounded-xl border px-3 py-3 text-left text-sm transition-colors ${
                      role === 'moderator'
                        ? 'border-alumni-600 bg-alumni-50 dark:bg-alumni-900/30 ring-1 ring-alumni-600'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span className="font-semibold block">副管理者</span>
                    <span className="text-xs text-gray-500">僅審核新進，不可代編</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole('owner')}
                    className={`rounded-xl border px-3 py-3 text-left text-sm transition-colors ${
                      role === 'owner'
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-500'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span className="font-semibold block">主管理者</span>
                    <span className="text-xs text-gray-500">全權（含設定與代編）</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="label">搜尋會員</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="search"
                    value={memberSearch}
                    onChange={(e) => {
                      setMemberSearch(e.target.value);
                      setSelected(null);
                    }}
                    placeholder="姓名、公司、科系、Email…"
                    className="input pl-10"
                    autoFocus
                  />
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 dark:border-gray-700 max-h-64 overflow-y-auto">
                {membersLoading ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    載入會員…
                  </div>
                ) : candidates.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    {memberSearch ? '找不到符合的會員' : '尚無可新增的會員（可能都已是管理員）'}
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                    {candidates.map((m) => {
                      const active = selected?.id === m.id;
                      return (
                        <li key={m.id}>
                          <button
                            type="button"
                            onClick={() => setSelected(m)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                              active
                                ? 'bg-alumni-50 dark:bg-alumni-900/30'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800/80'
                            }`}
                          >
                            <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0">
                              {m.avatarUrl ? (
                                <img
                                  src={m.avatarUrl}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-sm font-bold text-alumni-600">
                                  {(m.name || '?').charAt(0)}
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 dark:text-white truncate text-sm">
                                {m.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {[m.department, m.email].filter(Boolean).join(' · ') || '無科系／Email'}
                              </p>
                            </div>
                            {active && (
                              <span className="text-xs font-semibold text-alumni-600 shrink-0">
                                已選
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {selected && (
                <p className="text-sm text-gray-600 dark:text-gray-300 rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2">
                  將設定「<strong>{selected.name}</strong>」為
                  {role === 'owner' ? '主管理者' : '副管理者'}
                </p>
              )}
            </div>

            <div className="p-5 border-t border-gray-100 dark:border-gray-700 flex gap-3 justify-end shrink-0">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setSelected(null);
                }}
                className="btn-secondary"
              >
                取消
              </button>
              <button
                type="button"
                disabled={!selected || addMutation.isPending}
                onClick={handleAdd}
                className="btn-primary"
              >
                {addMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  '確認新增'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 移除確認 */}
      {confirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="card max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              移除管理員？
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              確定要移除「{confirmTarget.name || '此管理員'}」的
              {(confirmTarget.role || 'owner') === 'owner' ? '主' : '副'}管理者權限嗎？
              對方仍保留校友會員身分。
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setConfirmTarget(null)}
                disabled={removeMutation.isPending}
              >
                取消
              </button>
              <button
                type="button"
                className="btn-primary bg-red-600 hover:bg-red-700 border-red-600"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate(confirmTarget.id)}
              >
                {removeMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  '確認移除'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: Role }) {
  if (role === 'owner') {
    return (
      <span className="text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-800 dark:bg-amber-900/40 dark:text-amber-100 border border-amber-200 dark:border-amber-800">
        主管理者
      </span>
    );
  }
  return (
    <span className="text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 rounded-md bg-sky-50 text-sky-800 dark:bg-sky-900/40 dark:text-sky-100 border border-sky-200 dark:border-sky-800">
      副管理者
    </span>
  );
}
